import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeftRight, UserMinus, RotateCcw, Search } from "lucide-react";

const TIPOS = {
  reasignacion: { label: "Reasignación", icon: ArrowLeftRight, color: "#154360" },
  baja: { label: "Baja", icon: UserMinus, color: "#9F2241" },
  reactivacion: { label: "Reintegración", icon: RotateCcw, color: "#235B4E" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const grpLabel = (g) => g ? `${g.grado}° "${g.letra}"` : "—";

export default function MovimientosAlumnos() {
  const { user } = useAuth();
  const [planteles, setPlanteles] = useState([]);
  const [plantelId, setPlantelId] = useState("");
  const [tipo, setTipo] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/planteles");
        setPlanteles(data);
        if (data.length === 1) setPlantelId(data[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const params = { limit: 500 };
        if (plantelId) params.plantel_id = plantelId;
        if (tipo) params.tipo = tipo;
        const { data } = await api.get("/movimientos-alumno", { params });
        setItems(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [plantelId, tipo]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((m) =>
      (m.alumno_nombre || "").toLowerCase().includes(term) ||
      (m.alumno_curp || "").toLowerCase().includes(term) ||
      (m.user_name || "").toLowerCase().includes(term)
    );
  }, [items, q]);

  return (
    <AppLayout>
      <div className="max-w-6xl">
        <div className="mb-6">
          <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Auditoría</div>
          <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>Movimientos de alumnos</h1>
          <p className="text-sm text-[#545454] mt-1">
            Registro cronológico de <strong>reasignaciones de grupo</strong>, <strong>bajas</strong> y <strong>reintegraciones</strong> — con fecha y responsable.
          </p>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          {["superadmin", "jefe_sector", "supervisor_zona"].includes(user?.role) && planteles.length > 1 && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Plantel</label>
              <Select value={plantelId || "all"} onValueChange={(v) => setPlantelId(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="mov-plantel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos mis planteles</SelectItem>
                  {planteles.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Tipo de movimiento</label>
            <Select value={tipo || "all"} onValueChange={(v) => setTipo(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="mov-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="reasignacion">Reasignaciones</SelectItem>
                <SelectItem value="baja">Bajas</SelectItem>
                <SelectItem value="reactivacion">Reintegraciones</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Buscar (alumno / responsable)</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#9CA3AF]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, CURP o correo…" className="pl-8" data-testid="mov-search" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded overflow-hidden">
          <div className="px-4 py-2 text-xs uppercase tracking-widest text-[#6B7280] border-b border-[#F3F4F6]">
            {filtered.length} evento{filtered.length === 1 ? "" : "s"}
          </div>
          <ul className="divide-y divide-[#F3F4F6]">
            {loading ? (
              <li className="py-10 text-center text-sm text-[#9CA3AF]">Cargando…</li>
            ) : filtered.length === 0 ? (
              <li className="py-10 text-center text-sm text-[#9CA3AF]">Sin movimientos registrados con estos filtros</li>
            ) : filtered.map((m) => {
              const meta = TIPOS[m.tipo] || TIPOS.reasignacion;
              const Icon = meta.icon;
              return (
                <li key={m.id} className="flex items-start gap-3 py-3 px-4 hover:bg-[#FAFAFA]" data-testid={`mov-${m.id}`}>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: `${meta.color}15` }}>
                    <Icon className="w-4 h-4" style={{ color: meta.color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: meta.color }}>{meta.label}</span>
                      <span className="text-[10px] text-[#9CA3AF]">·</span>
                      <span className="text-[11px] text-[#6B7280]">{fmtDate(m.timestamp)}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-semibold uppercase text-[#111827]">{m.alumno_nombre || "—"}</span>
                      {m.alumno_curp && <span className="ml-2 text-[11px] font-mono text-[#9CA3AF]">{m.alumno_curp}</span>}
                    </div>
                    {m.tipo === "reasignacion" && (
                      <div className="text-[12px] text-[#374151] mt-0.5">
                        De <strong>{grpLabel(m.grupo_from)}</strong> a <strong>{grpLabel(m.grupo_to)}</strong>
                      </div>
                    )}
                    {m.tipo === "baja" && m.motivo && (
                      <div className="text-[12px] text-[#7C1E36] mt-0.5">Motivo: {m.motivo}</div>
                    )}
                    <div className="text-[11px] text-[#6B7280] mt-1">
                      Responsable: <span className="font-medium text-[#374151]">{m.user_name || m.user_email || "—"}</span>
                      {m.user_role && <span className="ml-1 text-[10px] uppercase tracking-wider text-[#9CA3AF]">({m.user_role.replace(/_/g, " ")})</span>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </AppLayout>
  );
}
